import type { Role } from '@/lib/auth-context';
import type { ModuleKey } from '@/lib/modules';
import { fetchApi } from '@/services/api-client';

export const USERS_PATH = '/admin/users';

export type UserAdminRow = {
  id: string;
  email: string;
  role: Role;
  active: boolean;
  linked: boolean;
  isWorkspace: boolean;
  createdAt: string;
  /** null when the account has never been signed in to. */
  lastLoginAt: string | null;
  modules: ModuleKey[];
  /** A till account: only the POS pages of its modules. */
  isPosOperator: boolean;
  personId: string | null;
  fullName: string | null;
  photoUrl: string;
  nickname: string | null;
  nationalities: { code: string; name: string; flag: string }[];
  passportOrThaiId: string | null;
  /** Paid vacation days per academic year. 0 = cannot request vacation leave. */
  vacationDays: number;
};

export type UserSortKey = 'email' | 'role' | 'status' | 'createdAt' | 'lastLoginAt';

export type UserAdminListResponse = {
  data: UserAdminRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type UsersFilters = {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: Role;
  active?: boolean;
  sortKey?: UserSortKey;
  sortDir?: 'asc' | 'desc';
};

export const usersApi = {
  list: () => fetchApi<UserAdminRow[]>(USERS_PATH),
  listPaged: (filters: UsersFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.page) params.append('page', String(filters.page));
    if (filters.pageSize) params.append('pageSize', String(filters.pageSize));
    if (filters.search) params.append('search', filters.search);
    if (filters.role) params.append('role', filters.role);
    if (filters.active !== undefined) params.append('status', String(filters.active));
    if (filters.sortKey) params.append('sortKey', filters.sortKey);
    if (filters.sortDir) params.append('sortDir', filters.sortDir);
    const qs = params.toString();
    return fetchApi<UserAdminListResponse>(`${USERS_PATH}/paged${qs ? `?${qs}` : ''}`);
  },
  create: (data: {
    email: string;
    role: Role;
    password?: string;
    modules?: ModuleKey[];
    isPosOperator?: boolean;
    personId?: string;
    fullName?: string;
    photoUrl?: string;
    nickname?: string | null;
    sendInvitation?: boolean;
    nationalities?: string[];
    passportOrThaiId?: string | null;
    vacationDays?: number;
  }) => fetchApi<UserAdminRow>(USERS_PATH, { method: 'POST', body: JSON.stringify(data) }),
  update: (
    id: string,
    data: {
      role?: Role;
      active?: boolean;
      modules?: ModuleKey[];
      isPosOperator?: boolean;
      personId?: string | null;
      fullName?: string | null;
      photoUrl?: string;
      nickname?: string | null;
      nationalities?: string[];
      passportOrThaiId?: string | null;
      vacationDays?: number;
    }
  ) => fetchApi<UserAdminRow>(`${USERS_PATH}/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  resendOnboarding: (id: string) =>
    fetchApi<{ success: true }>(`${USERS_PATH}/${id}/resend-onboarding`, { method: 'POST' }),
};
